export interface User {
  id: string;
  name: string;
  email: string;
  initials?: string;
  organization?: string;
  company?: string;
  role?: 'superAdmin' | 'admin' | 'projectManager' | 'stakeholder' | 'viewer';
  avatar?: string;
}

export interface Project {
  id: string;
  name: string;
  fullName?: string;
  description?: string;
  location: string;
  locationData?: { city?: string; address?: string; province?: string; postalCode?: string } | null;
  period?: string;
  startDate?: string;
  endDate?: string;
  status: ProjectStatus | string;
  statusLabel?: string;
  image?: string;
  headerImage?: string;
  icon?: React.ReactNode;
  settings?: {
    modules?: Record<string, boolean>;
  };
  metrics?: {
    sentimentScore: number;
    stakeholderCount: number;
    mediaReach: number;
    engagementRate: number;
  };
}

export enum ProjectStatus {
  UitvoeringKritiek = 'uitvoering-kritiek',
  Voorbereiding = 'voorbereiding',
  Uitvoering = 'uitvoering',
  Planfase = 'planfase',
  UitvoeringUrgent = 'uitvoering-urgent',
  UitvoeringStabiel = 'uitvoering-stabiel',
}

export interface DashboardMetric {
  icon: React.ReactNode;
  iconBackground: string;
  value: string | number;
  label: string;
  change?: {
    value: string;
    type: 'positive' | 'negative' | 'neutral';
  };
}

export interface BehavioralDeterminant {
  description: string;
  effect: string;
  leverage: string;
  source: string;
}

export interface Top3Determinant {
  name: string;
  reasoning: string;
  communicationImplication: string;
}

export interface Persona {
  id: string;
  name: string;
  age: number;
  occupation: string;
  description: string;
  concerns: string[];
  communication: string[];
  avatar?: string;
  initials?: string;
  type?: 'primary' | 'secondary' | 'tertiary';
  demographics?: {
    ageRange?: string;
    gender?: string;
    education?: string;
    occupation?: string;
    income?: string;
    location?: string;
    familyStatus?: string;
  };
  psychographics?: {
    interests?: string[];
    values?: string[];
    lifestyle?: string;
    personality?: string;
    motivations?: string[];
    frustrations?: string[];
    goals?: string[];
    fears?: string[];
  };
  behaviors?: {
    mediaConsumption?: string[];
    buyingBehavior?: string;
    communicationPreferences?: string[];
  };
  projectRelation?: {
    awarenessLevel?: string;
    sentiment?: string;
    influence?: string;
    engagement?: string;
  };
  // CASI-specific fields
  biography?: string;
  quote?: string;
  dailyRoutine?: string[];
  currentBehavior?: string;
  desiredBehavior?: string;
  triggers?: string[];
  momentOfTruth?: string;
  behavioralDeterminants?: {
    weerstand?: BehavioralDeterminant;
    zelfbeeld?: BehavioralDeterminant;
    automatismen?: BehavioralDeterminant;
    emoties?: BehavioralDeterminant;
    socialeOmgeving?: BehavioralDeterminant;
    fysiekeOmgeving?: BehavioralDeterminant;
    kunnen?: BehavioralDeterminant;
    kennis?: BehavioralDeterminant;
    houdingEnRisicoperceptie?: BehavioralDeterminant;
  };
  top3Determinants?: Top3Determinant[];
  interventionChecklist?: string[];
  qualityControl?: {
    stereotypeRisks?: string[];
    actionableQuestions?: string[];
  };
  sourcesAndDataQuality?: {
    projectSources?: string;
    assumptions?: string;
    knowledgeGaps?: string;
  };
  communicationPreferences?: string[];
  notes?: string;
}

export interface SentimentData {
  date: string;
  positive: number;
  neutral: number;
  negative: number;
}

export interface VoiceCategory {
  count: number;
  examples: string[];
  actionNeeded: string;
}

export interface SentimentAnalysis {
  overallEscalationLevel: number;
  topIssues: { issue: string; framing: string; severity: string }[];
  voiceCategories: {
    constructivelyCritical?: VoiceCategory;
    emotionalPrincipled?: VoiceCategory;
    factualInforming?: VoiceCategory;
    supportive?: VoiceCategory;
  };
  misinformation: string[];
  narrativeAnalysis: string;
  strategicAdvice: {
    webcareTone: string;
    stakeholderOutreach: string;
    messagingAdjustment: string;
  };
  lastAnalyzedAt?: string;
}

export interface NavigationItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  badge?: number;
}

export interface ActionCard {
  id: string;
  title: string;
  icon: React.ReactNode;
  onClick: () => void;
}

export interface Alert {
  id: string;
  type: 'info' | 'warning' | 'error' | 'success';
  title: string;
  message: string;
  timestamp?: Date;
}

export interface MediaItem {
  id: string;
  type: 'letter' | 'newsletter' | 'social' | 'presentation';
  title: string;
  description?: string;
  date?: string;
  status?: 'draft' | 'published' | 'scheduled';
}

export interface StakeholderActor {
  name: string;
  category: string;
  position: string;
  interest: string;
  exchangeSpace: string;
}

export interface ParticipationLevel {
  stakeholderGroup: string;
  level: 'informeren' | 'consulteren' | 'adviseren' | 'coproduceren' | 'meebeslissen';
  reasoning: string;
}

export interface StrategyItem {
  id: string;
  title: string;
  name?: string;
  description: string;
  type?: 'communication' | 'engagement' | 'crisis' | 'campaign' | 'content' | 'social_media' | 'pr' | 'other';
  priority: 'high' | 'medium' | 'low';
  status: 'planned' | 'in-progress' | 'completed' | 'draft' | 'review' | 'approved' | 'active' | 'paused' | 'archived';
  dueDate?: string;
  objectives?: { title: string; description: string; measurable?: string; deadline?: string; completed?: boolean }[];
  targetAudience?: { persona?: string; customDescription?: string; priority?: string }[];
  keyMessages?: { message: string; audience?: string; channel?: string; priority?: string }[];
  tactics?: { name: string; description?: string; channel?: string; status?: string }[];
  timeline?: { startDate?: string; endDate?: string; phases?: { name: string; startDate?: string; endDate?: string; milestones?: string[] }[] };
  budget?: { total?: number; allocated?: number; spent?: number; currency?: string };
  kpis?: { name: string; description?: string; target?: number; current?: number; unit?: string }[];
  risks?: { description: string; impact?: string; probability?: string; mitigation?: string }[];
  stakeholders?: { name: string; role?: string; influence?: string; interest?: string; engagementStrategy?: string }[];
  // SOM/MGA-specific fields
  theoreticalBasis?: string;
  stakeholderMapping?: {
    actors?: StakeholderActor[];
    interestMatrix?: string;
  };
  issueAnalysis?: {
    conflictPoints?: string[];
    gains?: string[];
  };
  coreMessage?: string;
  participationLevels?: ParticipationLevel[];
  notes?: string;
}

export type Module = 'dashboard' | 'gebiedsanalyse' | 'sentiment' | 'personas' | 'strategy' | 'media' | 'werkplanning' | 'vragen' | 'relatiebeheer' | 'intake' | 'projectwebsite' | 'issue-radar' | 'participatie' | 'rapportage' | 'stakeholder-netwerk' | 'vergunningen' | 'kennisbank';

export interface GeoJsonPoint {
  type: 'Point';
  coordinates: [number, number]; // [lng, lat]
}

export interface GeoJsonPolygon {
  type: 'Polygon';
  coordinates: number[][][];
}

export interface ProjectAreaData {
  _id: string;
  name: string;
  project: string;
  location: GeoJsonPoint;
  boundary: GeoJsonPolygon;
  bufferZone: GeoJsonPolygon;
  influenceRadius: number;
  createdAt: string;
  updatedAt: string;
}

export interface DemographicProfile {
  inwoners: number;
  mannen: number;
  vrouwen: number;
  percentage0Tot14: number;
  percentage15Tot24: number;
  percentage25Tot44: number;
  percentage45Tot64: number;
  percentage65Plus: number;
  gemiddeldInkomen: number;
  huurwoningen: number;
  koopwoningen: number;
  woningvoorraad: number;
  bevolkingsdichtheid: number;
  afstandHuisarts: number;
  afstandSupermarkt: number;
}

export interface StakeholderAdvies {
  type: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
}

export interface AdditionalDemographics {
  eenpersoonshuishoudens: number;
  huishoudensZonderKinderen: number;
  huishoudensMetKinderen: number;
  gemiddeldeWOZ: number;
  percentageWesters: number;
  percentageNietWesters: number;
}

export interface BedrijvenCategorieen {
  kantoren: number;
  retail: number;
  industrie: number;
  zorg: number;
  onderwijs: number;
  sport: number;
  horeca: number;
  overig: number;
}

export interface BedrijvenSector {
  sbiHoofdgroep: string;
  sbiOmschrijving: string;
  count: number;
  percentage: number;
}

export interface BedrijvenData {
  totaal: number;
  dichtheidPerKm2: number;
  categorieen: BedrijvenCategorieen;
  topSectoren: { naam: string; count: number }[];
  sectoren: BedrijvenSector[];
}

export interface BedrijvenAdvies {
  type: string;
  severity: 'high' | 'medium' | 'low';
  message: string;
  sector: string;
}

export interface PersonaHint {
  type: string;
  priority: 'high' | 'medium' | 'low';
  channel: string;
  reason: string;
}

// Voorzieningen (Overpass/OSM)
export interface VoorzieningItem {
  osmId: string;
  name: string;
  hasName?: boolean;
  category: string;
  subcategory: string;
  lat: number;
  lon: number;
  distance: number;
  tags?: Record<string, string>;
}

export interface CategoryStats {
  category: string;
  count: number;
  nearestDistance: number;
  averageDistance: number;
}

export interface VoorzieningenData {
  items: VoorzieningItem[];
  stats: CategoryStats[];
  searchRadius: number;
}

// Bedrijven via Overpass (richer than BAG)
export interface BedrijvenOverpassData {
  items: VoorzieningItem[];
  stats: CategoryStats[];
  totaal: number;
  searchRadius: number;
}

// Maatschappelijke Organisaties
export interface OrganisatiesData {
  items: VoorzieningItem[];
  stats: CategoryStats[];
  totaal: number;
  searchRadius: number;
}

// AI Gebiedsprofiel
export interface Gebiedsprofiel {
  narrative: string;
  highlights: string[];
  aandachtspunten: string[];
  generatedAt: string;
  model: string;
}

// Invloedsgebied analyse
export type WoningCategorie = 'hoogbouw' | 'laagbouw' | 'nieuwbouw' | 'jaren30' | 'naoorlogs' | 'gemengd';

export const WONING_CATEGORIE_LABELS: Record<WoningCategorie, string> = {
  hoogbouw: 'Hoogbouw',
  laagbouw: 'Laagbouw',
  nieuwbouw: 'Nieuwbouw',
  jaren30: 'Jaren 30',
  naoorlogs: 'Naoorlogs',
  gemengd: 'Gemengd',
};

export interface InvloedsgebiedZone {
  radius: number;
  label: string;
  aantalPanden: number;
  woningTypes: { type: string; count: number }[];
  categorieen: { key: string; label: string; count: number }[];
}

export interface CategorieOverzicht {
  key: string;
  label: string;
  description: string;
  aantal: number;
  dichtstbijMeter: number | null;
}

export interface InvloedsgebiedAnalyse {
  totaalPandenInGebied: number;
  dichtstbijzijndeWoning: {
    afstandMeter: number;
    bouwperiode: string;
    bouwjaar: number | null;
    categorieen: string[];
  } | null;
  zones: InvloedsgebiedZone[];
  categorieOverzicht: CategorieOverzicht[];
  spreiding: { noord: number; oost: number; zuid: number; west: number };
  activeFilters: string[];
  invloedsRadius: number;
}

// Raakvlakprojecten
export interface RaakvlakProject {
  id: string;
  naam: string;
  bron: string;
  bronType: 'werkzaamheden' | 'vergunning' | 'project';
  lat: number;
  lng: number;
  afstandMeter: number;
  startDate: string | null;
  endDate: string | null;
  hinderTypes: string[];
  status: string;
  url?: string;
}

export interface HinderOverlap {
  projectA: { id: string; naam: string };
  projectB: { id: string; naam: string };
  overlapPeriode: { van: string; tot: string; dagen: number };
  gedeeldeHinder: { type: string; label: string }[];
  gelijktijdigeHinder: boolean;
}

export interface CommunicatieRisico {
  niveau: 'hoog' | 'gemiddeld' | 'laag';
  titel: string;
  beschrijving: string;
  advies: string;
}

export interface RaakvlakAnalyse {
  totaalRaakvlakprojecten: number;
  zoekRadius: number;
  projecten: RaakvlakProject[];
  hinderAnalyse: {
    aantalActief: number;
    overlaps: HinderOverlap[];
    aantalOverlaps: number;
    gelijktijdigeHinder: boolean;
    hinderTypeTotaal: { type: string; label: string; count: number }[];
  };
  cumulatieveImpact: {
    score: number;
    niveau: 'hoog' | 'gemiddeld' | 'laag';
    factoren: string[];
    samenvatting: string;
  };
  communicatieRisicos: CommunicatieRisico[];
}

// Bevolkingscontext (structured CBS data)
export interface Bevolkingscontext {
  bevolkingsdichtheid: number;
  leeftijdsopbouw: Record<string, number>;
  huishoudensamenstelling: {
    eenpersoonshuishoudens: number;
    huishoudensZonderKinderen: number;
    huishoudensMetKinderen: number;
  } | null;
  gemiddeldInkomen: number;
  woningmarkt: {
    huurwoningen: number;
    koopwoningen: number;
    woningvoorraad: number;
    percentageHuur: number;
    percentageKoop: number;
    gemiddeldeWOZ: number;
  };
}

export interface AreaAnalysisResult {
  projectArea: ProjectAreaData;
  areaSqm: number;
  buurtInfo?: { buurtCode: string; buurtNaam: string; wijkCode: string; gemeentenaam: string };
  demographics: DemographicProfile | null;
  additionalDemographics?: AdditionalDemographics | null;
  bevolkingscontext?: Bevolkingscontext | null;
  buurtNaam: string | null;
  adviezen: StakeholderAdvies[];
  bedrijven?: BedrijvenData | null;
  bedrijvenAdviezen?: BedrijvenAdvies[];
  personaHints?: PersonaHint[];
  voorzieningen?: VoorzieningenData | null;
  bedrijvenOverpass?: BedrijvenOverpassData | null;
  organisaties?: OrganisatiesData | null;
  gebiedsprofiel?: Gebiedsprofiel | null;
}

export interface ProjectContext {
  projectId: string;
  projectName: string;
  location: {
    buurt?: string;
    gemeente?: string;
    polygon?: GeoJsonPolygon;
  };
  gebiedsdata?: {
    inwoners: number;
    leeftijdsverdeling: {
      '0-14': number;
      '15-24': number;
      '25-44': number;
      '45-64': number;
      '65+': number;
    };
    gemInkomen: number;
    woningtype: { huur: number; koop: number };
    bevolkingsdichtheid: number;
    voorzieningen: { afstandHuisarts: number; afstandSupermarkt: number };
    bedrijven?: BedrijvenData;
    additionalDemographics?: AdditionalDemographics;
  };
  personas?: { id: string; name: string; type: string; topDeterminants: string[]; channelPreference: string[] }[];
  activeStrategies?: string[];
  sentimentLevel?: 1 | 2 | 3 | 4 | 5;
  topIssues?: string[];
}

export interface GeocodeSuggestion {
  display: string;
  type: string;
  lat: number;
  lon: number;
}

export interface PublicWebsiteSection {
  enabled: boolean;
  order: number;
}

export interface PublicWebsiteSettings {
  enabled: boolean;
  slug: string;
  publishedAt?: string;
  lastUpdatedAt?: string;
  hero: {
    title?: string;
    subtitle?: string;
    heroImage?: string;
    ctaText?: string;
    ctaLink?: string;
    stats?: Array<{ value: string; label: string }>;
  };
  sections: {
    hero: PublicWebsiteSection;
    about: PublicWebsiteSection;
    gebiedsanalyse: PublicWebsiteSection;
    facts: PublicWebsiteSection;
    timeline: PublicWebsiteSection;
    media: PublicWebsiteSection;
    participation: PublicWebsiteSection;
    faq: PublicWebsiteSection;
    contact: PublicWebsiteSection;
    newsletter: PublicWebsiteSection;
  };
  about: {
    heading?: string;
    body?: string;
    showLocation?: boolean;
    image?: string;
  };
  facts: Array<{ value: string; label: string; suffix?: string }>;
  factsImage?: string;
  contactEmail?: string;
  contact: {
    heading?: string;
    description?: string;
    contactPerson?: {
      name?: string;
      role?: string;
      email?: string;
      phone?: string;
    };
    showForm?: boolean;
    image?: string;
  };
  participation: {
    heading?: string;
    body?: string;
    image?: string;
    events?: Array<{
      title: string;
      date: string;
      location: string;
      description?: string;
    }>;
  };
  newsletter: {
    heading?: string;
    description?: string;
  };
  theme: {
    primaryColor?: string;
    headerStyle?: 'light' | 'dark';
  };
  seo: {
    metaTitle?: string;
    metaDescription?: string;
    ogImage?: string;
  };
  analytics?: {
    totalViews: number;
    lastVisitedAt?: string;
  };
}

// Omgevingsdata types
export type MeldingBron = 'automatisch' | 'handmatig';
export type MeldingType = 'bewoner' | 'media' | 'gemeente' | 'monitoring';

export const MELDING_BRON_LABELS: Record<MeldingBron, string> = {
  automatisch: 'Automatisch (API)',
  handmatig: 'Handmatig ingevoerd',
};

export const MELDING_TYPE_LABELS: Record<MeldingType, string> = {
  bewoner: 'Bewoner',
  media: 'Media',
  gemeente: 'Gemeente',
  monitoring: 'Monitoring',
};

export interface Melding {
  id: number | string;
  text: string;
  status: 'open' | 'in_behandeling' | 'afgehandeld';
  lat: number;
  lng: number;
  category: string;
  subcategory: string;
  createdAt: string;
  bron?: MeldingBron;
  bronDetail?: string;
  meldingType?: MeldingType;
}

export type WerkCategorie = 'project' | 'raakvlak';
export type HinderType = 'geluid_nacht' | 'wegafsluiting' | 'omleiding' | 'trillingen' | 'stof_overlast' | 'verkeer';

export const WERK_CATEGORIE_LABELS: Record<WerkCategorie, string> = {
  project: 'Projectwerkzaamheden',
  raakvlak: 'Raakvlakproject',
};

export const HINDER_TYPE_LABELS: Record<HinderType, string> = {
  geluid_nacht: 'Geluidsoverlast (nacht)',
  wegafsluiting: 'Wegafsluiting',
  omleiding: 'Omleiding',
  trillingen: 'Trillingen',
  stof_overlast: 'Stof/sloopoverlast',
  verkeer: 'Verkeershinder',
};

export interface Werkzaamheid {
  id: string;
  description: string;
  lat: number;
  lng: number;
  source: 'SPIN' | 'MELVIN';
  startDate?: string;
  endDate?: string;
  werkCategorie?: WerkCategorie;
  hinderType?: HinderType;
  url?: string;
}

export interface ParkeerZone {
  id: string;
  name: string;
  type: 'betaald' | 'vergunning' | 'blauwe_zone' | 'garage' | 'pr';
  lat: number;
  lng: number;
}

export interface Vergunning {
  id: string;
  title: string;
  type: 'omgevingsvergunning' | 'verkeersbesluit' | 'evenement' | 'horeca' | 'kapvergunning' | 'overig';
  lat: number;
  lng: number;
  date: string;
  url?: string;
}

export interface OmgevingsData {
  meldingen: Melding[];
  werkzaamheden: Werkzaamheid[];
  parkeerdata: ParkeerZone[];
  vergunningen: Vergunning[];
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ChatSuggestion {
  id: string;
  text: string;
}

// Admin and Intake related types
export interface Client {
  id: string;
  name: string;
  contactPerson: string;
  email: string;
  phone: string;
  address?: string;
  licenseType: 'basic' | 'professional' | 'enterprise';
  availableModules: Module[];
  createdAt: Date;
  updatedAt: Date;
}

export interface ProjectManager {
  id: string;
  name: string;
  email: string;
  phone?: string;
  expertise: string[];
  availability: 'available' | 'busy' | 'unavailable';
}

export interface Document {
  id: string;
  name: string;
  type: 'blvc' | 'stakeholder' | 'planning' | 'policy' | 'other';
  size: number;
  uploadedAt: Date;
  uploadedBy: string;
  url?: string;
}

export interface IntakeProject {
  id: string;
  name: string;
  fullName: string;
  location: string;
  period: string;
  status: 'draft' | 'review' | 'approved' | 'active';
  clientId?: string;
  client?: Client;
  projectManagerId?: string;
  projectManager?: ProjectManager;
  description: string;
  objectives: string[];
  selectedModules: Module[];
  documents: Document[];
  context: {
    blvcPlan?: string;
    stakeholderList?: string;
    planning?: string;
    policyDocuments?: string[];
    notes?: string;
  };
  createdBy: string;
  createdAt: Date;
  updatedAt: Date;
  isDraft: boolean;
}